import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Globe, GripVertical, Eye, EyeOff, Palette, Type, Save, ExternalLink, Check, Plus, Trash2, ChevronUp, ChevronDown, Layout, Loader2, Upload, BarChart3, Link2, X, Image } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_ICONS = { hero: '🏠', about: '👤', listings: '🏢', testimonials: '⭐', contact: '✉️', social: '🔗' };

export default function WebsiteBuilder() {
  const [site, setSite] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subTab, setSubTab] = useState('template');
  const [editingSection, setEditingSection] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragFieldIdx, setDragFieldIdx] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [domainData, setDomainData] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [perfScore, setPerfScore] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const fileRef = useRef(null);

  const fetchSite = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/website`, { withCredentials: true }),
        axios.get(`${API}/website/templates`, { withCredentials: true }),
      ]);
      setSite(s.data);
      setTemplates(t.data);
      if (s.data.exists) setSubTab('sections');
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchSite(); }, [fetchSite]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API}/website/analytics`, { withCredentials: true }),
        axios.get(`${API}/website/performance-score`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setAnalytics(a.data);
      if (p.data) setPerfScore(p.data);
    } catch {}
  }, []);

  const fetchDomain = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/website/domain`, { withCredentials: true });
      setDomainData(res.data);
      setDomainInput(res.data.custom_domain || '');
    } catch {}
  }, []);

  useEffect(() => {
    if (subTab === 'analytics') fetchAnalytics();
    if (subTab === 'settings') fetchDomain();
  }, [subTab, fetchAnalytics, fetchDomain]);

  const createSite = async (templateId) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/website`, { template_id: templateId }, { withCredentials: true });
      setSite(res.data);
      setSubTab('sections');
      toast.success('Website created! Customize your sections.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const saveSections = async (sections) => {
    try {
      await axios.patch(`${API}/website/sections`, { sections }, { withCredentials: true });
      setSite(prev => ({ ...prev, sections }));
    } catch { toast.error('Failed to save'); }
  };

  const saveStyle = async (updates) => {
    try {
      const res = await axios.patch(`${API}/website/style`, updates, { withCredentials: true });
      setSite(prev => ({ ...prev, ...res.data }));
      toast.success('Saved!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const togglePublish = async () => {
    try {
      const res = await axios.patch(`${API}/website/publish`, {}, { withCredentials: true });
      setSite(prev => ({ ...prev, published: res.data.published }));
      toast.success(res.data.published ? 'Website published!' : 'Website unpublished');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveDomain = async () => {
    try {
      const res = await axios.patch(`${API}/website/domain`, { custom_domain: domainInput }, { withCredentials: true });
      setDomainData(res.data);
      toast.success(domainInput ? 'Custom domain saved!' : 'Domain removed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadQR = async () => {
    setQrLoading(true);
    try {
      const res = await axios.get(`${API}/website/qr-code`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
      const a = document.createElement('a');
      a.href = url; a.download = `krieger-qr-${site.slug}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('QR code downloaded!');
    } catch { toast.error('Failed to generate QR code'); }
    setQrLoading(false);
  };

  // Photo upload
  const uploadPhoto = async (file, sectionIdx, field) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/website/upload-photo`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const photoUrl = `${API.replace('/api', '')}${res.data.url}`;
      updateSectionContent(sectionIdx, { [field]: photoUrl });
      toast.success('Photo uploaded!');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  // DnD handlers
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e, targetIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) return;
    const sections = [...site.sections];
    const [moved] = sections.splice(dragIdx, 1);
    sections.splice(targetIdx, 0, moved);
    setSite(prev => ({ ...prev, sections }));
    saveSections(sections);
    setDragIdx(null);
  };
  const moveSection = (idx, dir) => {
    const sections = [...site.sections];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
    setSite(prev => ({ ...prev, sections }));
    saveSections(sections);
  };
  const toggleSection = (idx) => {
    const sections = [...site.sections];
    sections[idx].enabled = !sections[idx].enabled;
    setSite(prev => ({ ...prev, sections }));
    saveSections(sections);
  };
  const updateSectionContent = (idx, content) => {
    const sections = [...site.sections];
    sections[idx].content = { ...sections[idx].content, ...content };
    setSite(prev => ({ ...prev, sections }));
  };
  const saveCurrentSections = () => { saveSections(site.sections); toast.success('Sections saved!'); setEditingSection(null); };

  const addTestimonial = (sectionIdx) => {
    const sections = [...site.sections];
    const items = sections[sectionIdx].content.items || [];
    items.push({ id: Date.now().toString(), name: '', quote: '', title: '' });
    sections[sectionIdx].content.items = items;
    setSite(prev => ({ ...prev, sections }));
  };
  const removeTestimonial = (sectionIdx, itemIdx) => {
    const sections = [...site.sections];
    sections[sectionIdx].content.items.splice(itemIdx, 1);
    setSite(prev => ({ ...prev, sections }));
  };
  const updateTestimonial = (sectionIdx, itemIdx, field, value) => {
    const sections = [...site.sections];
    sections[sectionIdx].content.items[itemIdx][field] = value;
    setSite(prev => ({ ...prev, sections }));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#002FA7]" /></div>;

  const siteUrl = site?.slug ? `${window.location.origin}/agent/${site.slug}` : '';

  return (
    <div data-testid="website-builder">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">My Website</h1>
          <p className="text-sm text-slate-400">Build your branded agent website with drag-and-drop.</p>
        </div>
        {site?.exists && (
          <div className="flex items-center gap-2">
            <Button onClick={() => setPreviewOpen(true)} variant="outline" size="sm" className="text-xs h-8 gap-1" data-testid="preview-btn"><Eye size={12} /> Preview</Button>
            {site.published && siteUrl && (
              <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="text-xs h-8 gap-1" data-testid="view-site-btn"><ExternalLink size={12} /> View Live</Button>
              </a>
            )}
            <Button onClick={togglePublish} size="sm" className={`text-xs h-8 gap-1 ${site.published ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-[#002FA7] hover:bg-[#002075]'} text-white`} data-testid="publish-btn">
              {site.published ? <><Eye size={12} /> Published</> : <><EyeOff size={12} /> Publish</>}
            </Button>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      {site?.exists && (
        <div className="flex gap-1 mb-6 overflow-x-auto">
          {[
            { id: 'template', label: 'Template', icon: Layout },
            { id: 'sections', label: 'Sections', icon: GripVertical },
            { id: 'style', label: 'Style', icon: Palette },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'settings', label: 'Settings', icon: Type },
          ].map(t => (
            <button key={t.id} onClick={() => setSubTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${subTab === t.id ? 'bg-[#002FA7] text-white' : 'text-slate-500 bg-slate-100 hover:bg-slate-200'}`}
              data-testid={`builder-tab-${t.id}`}>
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ===== FULL-PAGE PREVIEW MODAL ===== */}
      {previewOpen && site?.slug && (
        <div className="fixed inset-0 z-50 bg-black/80" data-testid="preview-modal">
          <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">Preview Mode</Badge>
            <button onClick={() => setPreviewOpen(false)} className="w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg" data-testid="close-preview"><X size={16} className="text-slate-700" /></button>
          </div>
          <div className="absolute top-12 left-0 right-0 bottom-0 bg-white rounded-t-2xl overflow-hidden">
            {/* Browser chrome */}
            <div className="h-10 bg-slate-100 border-b border-slate-200 flex items-center px-4 gap-2">
              <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-amber-400" /><div className="w-3 h-3 rounded-full bg-emerald-400" /></div>
              <div className="flex-1 mx-4 bg-white rounded-md border border-slate-200 px-3 py-1 text-[10px] text-slate-500 truncate">{domainData?.custom_domain ? `https://${domainData.custom_domain}` : siteUrl}</div>
            </div>
            <iframe src={`/agent/${site.slug}?preview=true`} className="w-full" style={{ height: 'calc(100% - 40px)' }} title="Website Preview" data-testid="preview-iframe" />
          </div>
        </div>
      )}

      {/* ===== TEMPLATE PICKER ===== */}
      {(!site?.exists || subTab === 'template') && (
        <div data-testid="template-picker">
          {!site?.exists && <p className="text-sm text-slate-500 mb-4">Choose a template to get started:</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {templates.map(tpl => (
              <div key={tpl.id}
                className={`border-2 rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg ${site?.template_id === tpl.id ? 'border-[#002FA7] shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                onClick={() => site?.exists ? saveStyle({ template_id: tpl.id }) : createSite(tpl.id)}
                data-testid={`template-${tpl.id}`}>
                <div className="h-44 relative overflow-hidden" style={{ background: tpl.preview_color }}>
                  {tpl.preview_image ? (
                    <img src={tpl.preview_image} alt={tpl.name} className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: tpl.accent + '20' }}>
                          <Globe size={20} style={{ color: tpl.accent }} />
                        </div>
                        <div className="text-xs font-semibold" style={{ color: tpl.preview_color === '#FFFFFF' || tpl.preview_color === '#FFFBF0' || tpl.preview_color === '#FDFBF7' ? '#0F172A' : '#F1F5F9' }}>{tpl.name}</div>
                      </div>
                    </div>
                  )}
                  {site?.template_id === tpl.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#002FA7] flex items-center justify-center"><Check size={12} className="text-white" /></div>
                  )}
                </div>
                <div className="p-3 bg-white">
                  <div className="text-sm font-semibold text-slate-800">{tpl.name}</div>
                  <div className="text-[10px] text-slate-400">{tpl.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== SECTIONS (Drag & Drop) ===== */}
      {site?.exists && subTab === 'sections' && (
        <div data-testid="sections-editor">
          <div className="space-y-2 mb-4">
            {site.sections.map((section, idx) => (
              <div key={section.id}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, idx)}
                className={`bg-white border rounded-xl transition-all ${dragIdx === idx ? 'border-[#002FA7] shadow-md opacity-70' : 'border-slate-200 hover:border-slate-300'} ${!section.enabled ? 'opacity-50' : ''}`}
                data-testid={`section-${section.id}`}>
                <div className="flex items-center gap-3 p-3">
                  <div className="cursor-grab text-slate-300 hover:text-slate-500 shrink-0"><GripVertical size={16} /></div>
                  <span className="text-base shrink-0">{SECTION_ICONS[section.type] || '📄'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{section.label}</div>
                    <div className="text-[10px] text-slate-400 capitalize">{section.type}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30"><ChevronUp size={14} /></button>
                    <button onClick={() => moveSection(idx, 1)} disabled={idx === site.sections.length - 1} className="p-1 rounded hover:bg-slate-100 text-slate-400 disabled:opacity-30"><ChevronDown size={14} /></button>
                    <button onClick={() => toggleSection(idx)} className={`p-1 rounded hover:bg-slate-100 ${section.enabled ? 'text-emerald-500' : 'text-slate-300'}`} data-testid={`toggle-${section.id}`}>
                      {section.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button onClick={() => setEditingSection(editingSection === idx ? null : idx)} className="p-1 rounded hover:bg-slate-100 text-slate-400 text-[10px] font-medium">Edit</button>
                  </div>
                </div>

                {editingSection === idx && (
                  <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-3" data-testid={`editor-${section.id}`}>
                    {section.type === 'hero' && (
                      <>
                        <div><Label className="text-[10px] text-slate-400">Headline</Label><Input value={section.content.headline || ''} onChange={e => updateSectionContent(idx, { headline: e.target.value })} className="text-sm" /></div>
                        <div><Label className="text-[10px] text-slate-400">Subheadline</Label><Input value={section.content.subheadline || ''} onChange={e => updateSectionContent(idx, { subheadline: e.target.value })} className="text-sm" /></div>
                        <div><Label className="text-[10px] text-slate-400">CTA Button Text</Label><Input value={section.content.cta_text || ''} onChange={e => updateSectionContent(idx, { cta_text: e.target.value })} className="text-sm" /></div>
                        <div>
                          <Label className="text-[10px] text-slate-400">Hero Background Image</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input value={section.content.background_image || ''} onChange={e => updateSectionContent(idx, { background_image: e.target.value })} className="text-sm flex-1" placeholder="URL or upload →" />
                            <input type="file" accept="image/*" className="hidden" ref={fileRef} onChange={e => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0], idx, 'background_image'); }} />
                            <Button variant="outline" size="sm" className="text-xs h-8 gap-1 shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-hero-photo">
                              <Upload size={12} /> {uploading ? '...' : 'Upload'}
                            </Button>
                          </div>
                          {section.content.background_image && <img src={section.content.background_image} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
                        </div>
                      </>
                    )}
                    {section.type === 'about' && (
                      <>
                        <div><Label className="text-[10px] text-slate-400">Bio</Label><Textarea value={section.content.bio || ''} onChange={e => updateSectionContent(idx, { bio: e.target.value })} rows={3} className="text-sm" /></div>
                        <div>
                          <Label className="text-[10px] text-slate-400">Profile Photo</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input value={section.content.photo || ''} onChange={e => updateSectionContent(idx, { photo: e.target.value })} className="text-sm flex-1" placeholder="URL or upload →" />
                            <input type="file" accept="image/*" className="hidden" id={`about-photo-${idx}`} onChange={e => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0], idx, 'photo'); }} />
                            <Button variant="outline" size="sm" className="text-xs h-8 gap-1 shrink-0" onClick={() => document.getElementById(`about-photo-${idx}`)?.click()} disabled={uploading} data-testid="upload-about-photo">
                              <Upload size={12} /> {uploading ? '...' : 'Upload'}
                            </Button>
                          </div>
                          {section.content.photo && <img src={section.content.photo} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-[10px] text-slate-400">Years Experience</Label><Input value={section.content.years_experience || ''} onChange={e => updateSectionContent(idx, { years_experience: e.target.value })} className="text-sm" /></div>
                          <div><Label className="text-[10px] text-slate-400">Specialization</Label><Input value={section.content.specialization || ''} onChange={e => updateSectionContent(idx, { specialization: e.target.value })} className="text-sm" /></div>
                        </div>
                      </>
                    )}
                    {section.type === 'contact' && (
                      <>
                        <div><Label className="text-[10px] text-slate-400">Heading</Label><Input value={section.content.heading || ''} onChange={e => updateSectionContent(idx, { heading: e.target.value })} className="text-sm" /></div>
                        <div><Label className="text-[10px] text-slate-400">Description</Label><Textarea value={section.content.description || ''} onChange={e => updateSectionContent(idx, { description: e.target.value })} rows={2} className="text-sm" /></div>

                        {/* Custom Fields Editor */}
                        <div className="pt-2 border-t border-slate-100" data-testid="custom-fields-editor">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Custom Fields</Label>
                            <div className="flex gap-1">
                              {[
                                { label: '+ Budget', field: { id: `cf_${Date.now()}`, label: 'Budget Range', type: 'select', required: false, options: ['Under $200K', '$200K–$500K', '$500K–$1M', '$1M–$3M', '$3M+'] } },
                                { label: '+ Property', field: { id: `cf_${Date.now()+1}`, label: 'Property Type', type: 'select', required: false, options: ['Single Family', 'Condo/Co-op', 'Townhouse', 'Multi-Family', 'Commercial', 'Land'] } },
                                { label: '+ Timeline', field: { id: `cf_${Date.now()+2}`, label: 'Timeline', type: 'select', required: false, options: ['ASAP', '1–3 Months', '3–6 Months', '6–12 Months', 'Just Exploring'] } },
                                { label: '+ Text', field: { id: `cf_${Date.now()+3}`, label: 'Custom Question', type: 'text', required: false, placeholder: 'Your answer...' } },
                              ].map(preset => (
                                <button key={preset.label} onClick={() => {
                                  const fields = [...(section.content.custom_fields || []), { ...preset.field, id: `cf_${Date.now()}_${Math.random().toString(36).slice(2,6)}` }];
                                  updateSectionContent(idx, { custom_fields: fields });
                                }} className="text-[9px] px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors" data-testid={`add-field-${preset.label.replace(/[\s+]/g,'').toLowerCase()}`}>{preset.label}</button>
                              ))}
                            </div>
                          </div>
                          {(section.content.custom_fields || []).length === 0 && (
                            <p className="text-[10px] text-slate-400 py-2">No custom fields. Use the buttons above to add budget range, property type, timeline, or a custom text question.</p>
                          )}
                          <div className="space-y-2">
                            {(section.content.custom_fields || []).map((field, fi) => (
                              <div key={field.id}
                                draggable
                                onDragStart={(e) => { e.stopPropagation(); setDragFieldIdx(fi); }}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => {
                                  e.preventDefault(); e.stopPropagation();
                                  if (dragFieldIdx === null || dragFieldIdx === fi) return;
                                  const fields = [...(section.content.custom_fields || [])];
                                  const [moved] = fields.splice(dragFieldIdx, 1);
                                  fields.splice(fi, 0, moved);
                                  updateSectionContent(idx, { custom_fields: fields });
                                  setDragFieldIdx(null);
                                }}
                                onDragEnd={() => setDragFieldIdx(null)}
                                className={`bg-slate-50 rounded-lg p-3 space-y-2 transition-all ${dragFieldIdx === fi ? 'ring-2 ring-[#002FA7]/40 opacity-60' : ''}`}
                                data-testid={`custom-field-${fi}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5">
                                    <div className="cursor-grab text-slate-300 hover:text-slate-500" data-testid={`drag-field-${fi}`}><GripVertical size={12} /></div>
                                    <span className="text-[10px] text-slate-400 font-medium">{field.type === 'select' ? 'Dropdown' : 'Text'} Field</span>
                                  </div>
                                  <div className="flex items-center gap-0.5">
                                    <button onClick={() => {
                                      if (fi === 0) return;
                                      const fields = [...(section.content.custom_fields || [])];
                                      [fields[fi], fields[fi - 1]] = [fields[fi - 1], fields[fi]];
                                      updateSectionContent(idx, { custom_fields: fields });
                                    }} disabled={fi === 0} className="p-0.5 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30" data-testid={`field-up-${fi}`}><ChevronUp size={12} /></button>
                                    <button onClick={() => {
                                      const fields = section.content.custom_fields || [];
                                      if (fi >= fields.length - 1) return;
                                      const copy = [...fields];
                                      [copy[fi], copy[fi + 1]] = [copy[fi + 1], copy[fi]];
                                      updateSectionContent(idx, { custom_fields: copy });
                                    }} disabled={fi >= (section.content.custom_fields || []).length - 1} className="p-0.5 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30" data-testid={`field-down-${fi}`}><ChevronDown size={12} /></button>
                                    <button onClick={() => {
                                      const fields = (section.content.custom_fields || []).filter((_, i) => i !== fi);
                                      updateSectionContent(idx, { custom_fields: fields });
                                    }} className="p-0.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 ml-1" data-testid={`remove-field-${fi}`}><Trash2 size={12} /></button>
                                  </div>
                                </div>
                                <Input value={field.label} onChange={e => {
                                  const fields = [...(section.content.custom_fields || [])];
                                  fields[fi] = { ...fields[fi], label: e.target.value };
                                  updateSectionContent(idx, { custom_fields: fields });
                                }} placeholder="Field label" className="text-xs" />
                                {field.type === 'select' && (
                                  <Input value={(field.options || []).join(', ')} onChange={e => {
                                    const fields = [...(section.content.custom_fields || [])];
                                    fields[fi] = { ...fields[fi], options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) };
                                    updateSectionContent(idx, { custom_fields: fields });
                                  }} placeholder="Options (comma-separated)" className="text-xs" />
                                )}
                                {field.type === 'text' && (
                                  <Input value={field.placeholder || ''} onChange={e => {
                                    const fields = [...(section.content.custom_fields || [])];
                                    fields[fi] = { ...fields[fi], placeholder: e.target.value };
                                    updateSectionContent(idx, { custom_fields: fields });
                                  }} placeholder="Placeholder text" className="text-xs" />
                                )}
                                <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer">
                                  <input type="checkbox" checked={field.required || false} onChange={e => {
                                    const fields = [...(section.content.custom_fields || [])];
                                    fields[fi] = { ...fields[fi], required: e.target.checked };
                                    updateSectionContent(idx, { custom_fields: fields });
                                  }} className="rounded" />
                                  Required
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    {section.type === 'testimonials' && (
                      <>
                        {(section.content.items || []).map((item, ti) => (
                          <div key={item.id || ti} className="bg-slate-50 rounded-lg p-3 space-y-2">
                            <div className="flex justify-between"><span className="text-[10px] text-slate-400">Review #{ti + 1}</span><button onClick={() => removeTestimonial(idx, ti)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button></div>
                            <Input value={item.name || ''} onChange={e => updateTestimonial(idx, ti, 'name', e.target.value)} placeholder="Client name" className="text-xs" />
                            <Input value={item.title || ''} onChange={e => updateTestimonial(idx, ti, 'title', e.target.value)} placeholder="Title (e.g. Homebuyer)" className="text-xs" />
                            <Textarea value={item.quote || ''} onChange={e => updateTestimonial(idx, ti, 'quote', e.target.value)} placeholder="Their review..." rows={2} className="text-xs" />
                          </div>
                        ))}
                        <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => addTestimonial(idx)}><Plus size={10} /> Add Review</Button>
                      </>
                    )}
                    {section.type === 'listings' && <p className="text-xs text-slate-400">Listings are automatically pulled from your dashboard. Up to {section.content.show_count || 6} active listings will display.</p>}
                    {section.type === 'social' && <p className="text-xs text-slate-400">Social links are pulled from your profile. Update them in the Marketing tab.</p>}
                    <Button onClick={saveCurrentSections} size="sm" className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs gap-1" data-testid="save-sections-btn"><Save size={12} /> Save Changes</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== STYLE CUSTOMIZER ===== */}
      {site?.exists && subTab === 'style' && (
        <div className="space-y-4" data-testid="style-editor">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Colors</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[{ key: 'bg', label: 'Background' }, { key: 'text', label: 'Text' }, { key: 'accent', label: 'Accent' }, { key: 'card_bg', label: 'Card' }].map(c => (
                <div key={c.key}>
                  <Label className="text-[10px] text-slate-400">{c.label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={site.custom_style?.[c.key] || '#000000'} onChange={e => setSite(prev => ({ ...prev, custom_style: { ...prev.custom_style, [c.key]: e.target.value } }))} className="w-8 h-8 rounded border border-slate-200 cursor-pointer" data-testid={`color-${c.key}`} />
                    <Input value={site.custom_style?.[c.key] || ''} onChange={e => setSite(prev => ({ ...prev, custom_style: { ...prev.custom_style, [c.key]: e.target.value } }))} className="text-xs flex-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Typography</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] text-slate-400">Heading Font</Label><Input value={site.custom_style?.font_heading || ''} onChange={e => setSite(prev => ({ ...prev, custom_style: { ...prev.custom_style, font_heading: e.target.value } }))} className="text-sm" data-testid="font-heading" /></div>
              <div><Label className="text-[10px] text-slate-400">Body Font</Label><Input value={site.custom_style?.font_body || ''} onChange={e => setSite(prev => ({ ...prev, custom_style: { ...prev.custom_style, font_body: e.target.value } }))} className="text-sm" data-testid="font-body" /></div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Border Radius</h3>
            <Input value={site.custom_style?.border_radius || '12px'} onChange={e => setSite(prev => ({ ...prev, custom_style: { ...prev.custom_style, border_radius: e.target.value } }))} className="text-sm w-40" data-testid="border-radius" />
          </div>
          <Button onClick={() => saveStyle({ custom_style: site.custom_style })} className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs gap-1" data-testid="save-style-btn"><Save size={12} /> Save Style</Button>
        </div>
      )}

      {/* ===== LEAD ANALYTICS ===== */}
      {site?.exists && subTab === 'analytics' && (
        <div className="space-y-4" data-testid="lead-analytics">
          {analytics ? (
            <>
              {/* Performance Score Card */}
              {perfScore && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="performance-score">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Website Performance Score</h3>
                    <div className="flex items-center gap-2">
                      <div className={`text-2xl font-['Cabinet_Grotesk'] font-extrabold ${perfScore.score >= 80 ? 'text-emerald-600' : perfScore.score >= 60 ? 'text-amber-500' : 'text-red-500'}`}>
                        {perfScore.grade}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-slate-800">{perfScore.score}<span className="text-xs text-slate-400">/{perfScore.max_score}</span></div>
                      </div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div className={`h-full rounded-full transition-all duration-700 ${perfScore.score >= 80 ? 'bg-emerald-500' : perfScore.score >= 60 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${perfScore.score}%` }} />
                  </div>
                  {/* Checks */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-4">
                    {perfScore.checks.map((c, i) => (
                      <div key={i} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] ${c.passed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}>
                        {c.passed ? <Check size={10} className="text-emerald-500 shrink-0" /> : <X size={10} className="text-slate-300 shrink-0" />}
                        <span className="truncate">{c.label}</span>
                        <span className="ml-auto shrink-0 font-medium">{c.points}pt</span>
                      </div>
                    ))}
                  </div>
                  {/* Improvement Tips */}
                  {perfScore.tips.length > 0 && (
                    <div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-2">Improvement Tips</div>
                      <div className="space-y-1.5">
                        {perfScore.tips.slice(0, 4).map((tip, i) => (
                          <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${tip.priority === 'high' ? 'bg-red-50 border-red-200 text-red-700' : tip.priority === 'medium' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-testid={`tip-${i}`}>
                            <span className="shrink-0 mt-0.5">{tip.priority === 'high' ? '🔴' : tip.priority === 'medium' ? '🟡' : '🟢'}</span>
                            {tip.tip}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Total Views</div>
                  <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{analytics.total_views}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Total Leads</div>
                  <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{analytics.total_leads}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Conversion</div>
                  <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">{analytics.conversion_rate}%</div>
                  <div className="text-[9px] text-slate-400">views → leads</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Status</div>
                  <div className={`text-lg font-bold mt-1 ${analytics.published ? 'text-emerald-600' : 'text-amber-500'}`}>{analytics.published ? 'Live' : 'Draft'}</div>
                </div>
              </div>

              {/* QR Code + Share */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="qr-section">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">QR Code</h3>
                  <p className="text-[10px] text-slate-400">Download a QR code for your business cards and flyers</p>
                </div>
                <Button onClick={downloadQR} disabled={qrLoading} size="sm" className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs h-8 gap-1.5" data-testid="download-qr-btn">
                  <Image size={12} /> {qrLoading ? 'Generating...' : 'Download QR Code'}
                </Button>
              </div>

              {/* Weekly Trend Chart */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 font-['Cabinet_Grotesk']">Weekly Lead Trend</h3>
                <div className="flex items-end gap-2 h-32">
                  {analytics.weekly_trend.map((w, i) => {
                    const max = Math.max(...analytics.weekly_trend.map(x => x.leads), 1);
                    const h = Math.max(4, (w.leads / max) * 100);
                    return (
                      <div key={i} className="flex flex-col items-center gap-0.5 flex-1 min-w-0">
                        <span className="text-[9px] text-slate-500 font-medium">{w.leads}</span>
                        <div className="w-full rounded-t-md bg-[#002FA7]/70" style={{ height: `${h}px` }} />
                        <span className="text-[8px] text-slate-400 truncate">{w.week}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Leads */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 font-['Cabinet_Grotesk']">Recent Website Leads</h3>
                {analytics.recent_leads.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">No leads yet. Share your website to start getting contacts!</p>
                ) : (
                  <div className="space-y-1.5">
                    {analytics.recent_leads.map((lead, i) => (
                      <div key={lead.id || i} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100">
                        <div>
                          <div className="text-xs font-medium text-slate-800">{lead.name}</div>
                          <div className="text-[10px] text-slate-400">{lead.email}{lead.phone ? ` · ${lead.phone}` : ''}</div>
                        </div>
                        <div className="text-[9px] text-slate-400">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[#002FA7]" /></div>
          )}
        </div>
      )}

      {/* ===== SETTINGS + CUSTOM DOMAIN ===== */}
      {site?.exists && subTab === 'settings' && (
        <div className="space-y-4" data-testid="site-settings">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Website URL</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">{window.location.origin}/agent/</span>
              <Input value={site.slug || ''} onChange={e => setSite(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} className="text-sm w-48" data-testid="slug-input" />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Subdomain: <span className="font-medium">{site.slug}.joinkrieger.com</span></p>
          </div>

          {/* Custom Domain */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="custom-domain-panel">
            <div className="flex items-center gap-2 mb-3">
              <Link2 size={14} className="text-[#002FA7]" />
              <h3 className="text-sm font-semibold text-slate-800">Custom Domain</h3>
            </div>
            <p className="text-xs text-slate-400 mb-3">Connect your own domain (e.g., joelkrieger.com) to your agent website.</p>
            <div className="flex items-center gap-2">
              <Input value={domainInput} onChange={e => setDomainInput(e.target.value.toLowerCase())} placeholder="e.g. joelkrieger.com" className="text-sm flex-1" data-testid="domain-input" />
              <Button onClick={saveDomain} size="sm" className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs h-8" data-testid="save-domain-btn">Save</Button>
              {domainInput && <Button onClick={() => { setDomainInput(''); saveDomain(); }} variant="outline" size="sm" className="text-xs h-8 text-red-500">Remove</Button>}
            </div>
            {domainData?.dns_instructions && (
              <div className="mt-3 p-3 bg-[#EFF3FF] border border-[#002FA7]/10 rounded-lg" data-testid="dns-instructions">
                <div className="text-[10px] font-semibold text-[#002FA7] uppercase tracking-wider mb-1">DNS Setup Required</div>
                <div className="text-xs text-slate-600 space-y-1">
                  <p>Add the following DNS record to your domain provider:</p>
                  <div className="bg-white rounded-md p-2 font-mono text-[10px] text-slate-700 border border-slate-200">
                    <span className="text-slate-400">Type:</span> {domainData.dns_instructions.type}<br />
                    <span className="text-slate-400">Host:</span> {domainData.dns_instructions.host}<br />
                    <span className="text-slate-400">Value:</span> {domainData.dns_instructions.value}
                  </div>
                  <p className="text-[10px] text-slate-400">{domainData.dns_instructions.note}</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">SEO</h3>
            <div className="space-y-3">
              <div><Label className="text-[10px] text-slate-400">Page Title</Label><Input value={site.page_title || ''} onChange={e => setSite(prev => ({ ...prev, page_title: e.target.value }))} className="text-sm" data-testid="page-title" /></div>
              <div><Label className="text-[10px] text-slate-400">Meta Description</Label><Textarea value={site.meta_description || ''} onChange={e => setSite(prev => ({ ...prev, meta_description: e.target.value }))} rows={2} className="text-sm" data-testid="meta-desc" /></div>
            </div>
          </div>
          <Button onClick={() => saveStyle({ slug: site.slug, page_title: site.page_title, meta_description: site.meta_description })} className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs gap-1" data-testid="save-settings-btn"><Save size={12} /> Save Settings</Button>
        </div>
      )}
    </div>
  );
}
